import Event from "sap/ui/base/Event";
import Control from "sap/ui/core/Control";
import JSONModel from "sap/ui/model/json/JSONModel";
import Dialog from "sap/m/Dialog";
import MessageBox from "sap/m/MessageBox";
import MessageToast from "sap/m/MessageToast";
import BaseController from "./BaseController";
import GroupsService, { type GroupRecord } from "../service/GroupsService";
import OidcService, {
	type OidcApi,
	type OidcApp,
	type OidcAuthMethod,
	type OidcStatus,
} from "../service/OidcService";

interface AppRow extends OidcApp {
	typeText: string;
	redirectText: string;
	apiText: string;
	lastUsedText: string;
}

interface ApiRow extends OidcApi {
	groupText: string;
	lifetimeText: string;
}

interface AppEditModel {
	id: number | null;
	name: string;
	clientId: string;
	authMethod: OidcAuthMethod;
	/** One URI per line. */
	redirectUris: string;
	postLogoutRedirectUris: string;
	resourceKeys: string[];
	allowRefresh: boolean;
	isActive: boolean;
}

interface ApiEditModel {
	id: number | null;
	name: string;
	identifier: string;
	scope: string;
	roleKey: string;
	groupKeys: string[];
	lifetimeMinutes: number;
	isActive: boolean;
}

const lines = (value: string): string[] =>
	value
		.split(/\r?\n/)
		.map((line) => line.trim())
		.filter(Boolean);

/**
 * Settings → Connected apps: the systems that sign users in with the portal
 * (OpenID Connect clients) and the APIs they may get access tokens for.
 * Embedded in the Settings view; Settings calls refreshPage() when the tab
 * is opened.
 *
 * @namespace com.ang.portal.controller
 */
export default class ConnectedApps extends BaseController {
	public onInit(): void {
		this.setModel(
			new JSONModel({ busy: false, status: null, statusText: "", apps: [], apis: [], groups: [] }),
			"oidcPage",
		);
		this.setModel(new JSONModel({}), "oidcApp");
		this.setModel(new JSONModel({}), "oidcApi");
		this.setModel(new JSONModel({ clientId: "", clientSecret: "" }), "oidcSecret");
	}

	private pageModel(): JSONModel {
		return this.getModel("oidcPage") as JSONModel;
	}

	public async refreshPage(): Promise<void> {
		const page = this.pageModel();
		page.setProperty("/busy", true);
		try {
			const bundle = await this.getResourceBundle();
			const [status, apps, apis, groups] = await Promise.all([
				OidcService.getStatus(),
				OidcService.listApps(),
				OidcService.listApis(),
				// Only names for the group picker; the page works without them.
				GroupsService.getGroups().catch((): GroupRecord[] => []),
			]);
			const groupNames = new Map(groups.map((group) => [group.id, group.name]));
			const apiNames = new Map(apis.map((api) => [api.id, api.name]));

			page.setProperty("/status", status);
			page.setProperty("/statusText", this.statusText(status, bundle.getText.bind(bundle)));
			page.setProperty(
				"/groups",
				groups.map((group) => ({ id: group.id, name: group.name })),
			);
			page.setProperty(
				"/apis",
				apis.map(
					(api): ApiRow => ({
						...api,
						groupText: api.allowedGroupIds.length
							? api.allowedGroupIds.map((id) => groupNames.get(id) ?? `#${id}`).join(", ")
							: bundle.getText("oidcAllUsers"),
						lifetimeText: bundle.getText("oidcMinutes", [Math.round(api.accessTokenTtl / 60)]),
					}),
				),
			);
			page.setProperty(
				"/apps",
				apps.map(
					(app): AppRow => ({
						...app,
						typeText: bundle.getText(app.authMethod === "none" ? "oidcTypePublic" : "oidcTypeConfidential"),
						redirectText: app.redirectUris.join("\n"),
						apiText: app.resourceIds.map((id) => apiNames.get(id) ?? `#${id}`).join(", "),
						lastUsedText: app.lastUsedAt ? new Date(app.lastUsedAt).toLocaleString() : "",
					}),
				),
			);
		} catch (error) {
			await this.showToastForError(error, "oidcLoadError");
		} finally {
			page.setProperty("/busy", false);
		}
	}

	private statusText(status: OidcStatus, getText: (key: string) => string): string {
		if (status.enabled) return "";
		switch (status.reason) {
			case "no_public_url":
				return getText("oidcStatusNoPublicUrl");
			case "no_encryption_key":
				return getText("oidcStatusNoEncryptionKey");
			default:
				return getText("oidcStatusFailed");
		}
	}

	private rowOf<T>(oEvent: Event): T | null {
		const source = oEvent.getSource();
		if (!(source instanceof Control)) return null;
		return (source.getBindingContext("oidcPage")?.getObject() as T | undefined) ?? null;
	}

	private async confirm(key: string, args: string[]): Promise<boolean> {
		const bundle = await this.getResourceBundle();
		return new Promise<boolean>((resolve) => {
			MessageBox.confirm(bundle.getText(key, args), {
				actions: [MessageBox.Action.OK, MessageBox.Action.CANCEL],
				emphasizedAction: MessageBox.Action.OK,
				onClose: (action) => resolve(action === "OK"),
			});
		});
	}

	private async run(action: () => Promise<void>, errorKey: string): Promise<void> {
		const page = this.pageModel();
		page.setProperty("/busy", true);
		try {
			await action();
		} catch (error) {
			await this.showToastForError(error, errorKey);
		} finally {
			page.setProperty("/busy", false);
		}
	}

	public async onCopyIssuer(): Promise<void> {
		const issuer = this.pageModel().getProperty("/status/issuer") as string;
		try {
			await navigator.clipboard.writeText(issuer);
			await this.showToast("oidcCopied");
		} catch {
			MessageToast.show(issuer);
		}
	}

	public async onRotateKey(): Promise<void> {
		if (!(await this.confirm("oidcRotateKeyConfirm", []))) return;
		await this.run(async () => {
			await OidcService.rotateSigningKey();
			await this.showToast("oidcRotateKeyDone");
		}, "oidcSaveError");
	}

	// ── Applications ─────────────────────────────────────────────────

	public onAddApp(): void {
		const data: AppEditModel = {
			id: null,
			name: "",
			clientId: "",
			authMethod: "client_secret_basic",
			redirectUris: "",
			postLogoutRedirectUris: "",
			resourceKeys: [],
			allowRefresh: true,
			isActive: true,
		};
		(this.getModel("oidcApp") as JSONModel).setData(data);
		(this.byId("oidcAppDialog") as Dialog).open();
	}

	public onEditApp(oEvent: Event): void {
		const app = this.rowOf<AppRow>(oEvent);
		if (!app) return;
		const data: AppEditModel = {
			id: app.id,
			name: app.name,
			clientId: app.clientId,
			authMethod: app.authMethod,
			redirectUris: app.redirectUris.join("\n"),
			postLogoutRedirectUris: app.postLogoutRedirectUris.join("\n"),
			resourceKeys: app.resourceIds.map(String),
			allowRefresh: app.allowRefresh,
			isActive: app.isActive,
		};
		(this.getModel("oidcApp") as JSONModel).setData(data);
		(this.byId("oidcAppDialog") as Dialog).open();
	}

	public onCloseAppDialog(): void {
		(this.byId("oidcAppDialog") as Dialog).close();
	}

	public async onSaveApp(): Promise<void> {
		const data = (this.getModel("oidcApp") as JSONModel).getData() as AppEditModel;
		if (!data.name.trim() || lines(data.redirectUris).length === 0) {
			await this.showToast("oidcValidationApp");
			return;
		}
		const payload: Partial<OidcApp> = {
			name: data.name.trim(),
			authMethod: data.authMethod,
			redirectUris: lines(data.redirectUris),
			postLogoutRedirectUris: lines(data.postLogoutRedirectUris),
			resourceIds: data.resourceKeys.map(Number),
			allowRefresh: data.allowRefresh,
			isActive: data.isActive,
		};
		await this.run(async () => {
			const result = data.id
				? await OidcService.updateApp(data.id, payload)
				: await OidcService.createApp({ ...payload, clientId: data.clientId.trim() || undefined });
			this.onCloseAppDialog();
			await this.refreshPage();
			if (result.clientSecret) this.openSecretDialog(result.item.clientId, result.clientSecret);
			else await this.showToast("oidcSaved");
		}, "oidcSaveError");
	}

	public async onNewSecret(oEvent: Event): Promise<void> {
		const app = this.rowOf<AppRow>(oEvent);
		if (!app || !(await this.confirm("oidcNewSecretConfirm", [app.name]))) return;
		await this.run(async () => {
			const { clientSecret } = await OidcService.rotateAppSecret(app.id);
			this.openSecretDialog(app.clientId, clientSecret);
		}, "oidcSaveError");
	}

	public async onRevokeSignIns(oEvent: Event): Promise<void> {
		const app = this.rowOf<AppRow>(oEvent);
		if (!app || !(await this.confirm("oidcRevokeConfirm", [app.name]))) return;
		await this.run(async () => {
			await OidcService.revokeAppSignIns(app.id);
			await this.showToast("oidcRevoked");
		}, "oidcSaveError");
	}

	public async onDeleteApp(oEvent: Event): Promise<void> {
		const app = this.rowOf<AppRow>(oEvent);
		if (!app || !(await this.confirm("oidcDeleteAppConfirm", [app.name]))) return;
		await this.run(async () => {
			await OidcService.deleteApp(app.id);
			await this.refreshPage();
		}, "oidcSaveError");
	}

	// ── APIs ─────────────────────────────────────────────────────────

	public onAddApi(): void {
		const data: ApiEditModel = {
			id: null,
			name: "",
			identifier: "",
			scope: "",
			roleKey: "",
			groupKeys: [],
			lifetimeMinutes: 15,
			isActive: true,
		};
		(this.getModel("oidcApi") as JSONModel).setData(data);
		(this.byId("oidcApiDialog") as Dialog).open();
	}

	public onEditApi(oEvent: Event): void {
		const api = this.rowOf<ApiRow>(oEvent);
		if (!api) return;
		const data: ApiEditModel = {
			id: api.id,
			name: api.name,
			identifier: api.identifier,
			scope: api.scope,
			roleKey: api.roleKey,
			groupKeys: api.allowedGroupIds.map(String),
			lifetimeMinutes: Math.round(api.accessTokenTtl / 60),
			isActive: api.isActive,
		};
		(this.getModel("oidcApi") as JSONModel).setData(data);
		(this.byId("oidcApiDialog") as Dialog).open();
	}

	public onCloseApiDialog(): void {
		(this.byId("oidcApiDialog") as Dialog).close();
	}

	public async onSaveApi(): Promise<void> {
		const data = (this.getModel("oidcApi") as JSONModel).getData() as ApiEditModel;
		if (!data.name.trim() || !data.identifier.trim() || !data.scope.trim()) {
			await this.showToast("oidcValidationApi");
			return;
		}
		const payload: Partial<OidcApi> = {
			name: data.name.trim(),
			identifier: data.identifier.trim(),
			scope: data.scope.trim(),
			roleKey: data.roleKey.trim() || data.scope.trim(),
			allowedGroupIds: data.groupKeys.map(Number),
			accessTokenTtl: Math.round(Number(data.lifetimeMinutes) * 60),
			isActive: data.isActive,
		};
		await this.run(async () => {
			if (data.id) await OidcService.updateApi(data.id, payload);
			else await OidcService.createApi(payload);
			this.onCloseApiDialog();
			await this.refreshPage();
			await this.showToast("oidcSaved");
		}, "oidcSaveError");
	}

	public async onDeleteApi(oEvent: Event): Promise<void> {
		const api = this.rowOf<ApiRow>(oEvent);
		if (!api || !(await this.confirm("oidcDeleteApiConfirm", [api.name]))) return;
		await this.run(async () => {
			await OidcService.deleteApi(api.id);
			await this.refreshPage();
		}, "oidcSaveError");
	}

	// ── One-time secret ──────────────────────────────────────────────

	private openSecretDialog(clientId: string, clientSecret: string): void {
		(this.getModel("oidcSecret") as JSONModel).setData({ clientId, clientSecret });
		(this.byId("oidcSecretDialog") as Dialog).open();
	}

	public onCloseSecretDialog(): void {
		(this.byId("oidcSecretDialog") as Dialog).close();
		// The secret is shown once; do not keep it around.
		(this.getModel("oidcSecret") as JSONModel).setData({ clientId: "", clientSecret: "" });
	}

	public async onCopySecret(): Promise<void> {
		const secret = (this.getModel("oidcSecret") as JSONModel).getProperty("/clientSecret") as string;
		if (!secret) return;
		try {
			await navigator.clipboard.writeText(secret);
			await this.showToast("oidcCopied");
		} catch {
			MessageToast.show(secret);
		}
	}
}
